import type { ToastAction } from '../state/toasts'

/** Loads one of the app's own chunks; `failures` counts the tries that didn't fetch it. */
export interface ChunkLoader<T> {
  (): Promise<T>
  failures: number
  /** Forgets the chunk it loaded, so the next call fetches it again. For tests. */
  forget: () => void
}

/**
 * Loads a chunk once, from a source kept apart so tests can stand in for it. After a failure (offline, or an update
 * replaced the chunk) the next try fetches it again.
 */
export function loadOnce<T>(source: { load: () => Promise<T> }): ChunkLoader<T> {
  let loading: Promise<T> | null = null
  const loader = (() => {
    loading ??= source.load().catch((error: unknown) => {
      loading = null
      loader.failures++
      throw error
    })
    return loading
  }) as ChunkLoader<T>
  loader.failures = 0
  loader.forget = () => {
    loading = null
    loader.failures = 0
  }
  return loader
}

/**
 * Whether a chunk that won't load means the app itself is out of date: a released version replaces every chunk, so the
 * ones this page knows about are gone for good and only opening the app again brings them back.
 */
export function outdatedApp(loader: ChunkLoader<unknown>): boolean {
  return loader.failures > 1
}

/** Reloading the app, offered on a toast for something it couldn't fetch. */
const reloadAppAction: ToastAction = { label: 'Reload', onClick: () => window.location.reload() }

/** How to say a chunk wouldn't load: once trying again has not helped, the toast offers the newest version of the app. */
export function reloadOption(loader: ChunkLoader<unknown>): { action: ToastAction; duration: number } | undefined {
  return outdatedApp(loader) ? { action: reloadAppAction, duration: 8000 } : undefined
}
